import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';

import AppLayout from '../../components/layout';
import { useLanguage } from '../../contexts/LanguageContext';
import debugLogger from '../../utils/debug';
import { stopMediaStream } from '../../utils/cameraControls';
import { getConfig, uploadDocuments } from '../../services/faceLiveness';
import CameraCapture from './CameraCapture';
import DocumentTypeSelection from './DocumentTypeSelection';
import { captureModeFor, overlayTypeFor, requiredSidesFor } from './documentTypes';
import { buildDocumentFormData, createCaptureSessionId, uploadErrorKey } from './uploadPayload';
import './index.css';

/**
 * Document capture flow.
 *
 * Document type -> permission -> capture each required side -> upload straight
 * to the IDV service -> hand over to Face Liveness.
 *
 * The upload goes browser-to-IDV directly: document bytes never traverse
 * Certage Backend. Certage only mints the short-lived, scoped upload token.
 *
 * This lives on its own route on purpose. The liveness page installs a global
 * getUserMedia patch that forces the front camera, so document capture must
 * never share a mount with it.
 */

export const STEPS = {
  SELECT_DOCUMENT_TYPE: 'select_document_type',
  CAMERA_PERMISSION: 'camera_permission',
  CAPTURE: 'capture',
  UPLOADING: 'uploading',
  COMPLETE: 'complete',
};

function DocumentCapture() {
  const { sessionId } = useParams();
  const { t } = useLanguage();
  const copy = t.documentCapture;

  const [step, setStep] = useState(STEPS.SELECT_DOCUMENT_TYPE);
  const [documentType, setDocumentType] = useState(null);
  const [sideIndex, setSideIndex] = useState(0);
  const [captures, setCaptures] = useState({});
  const [permissionError, setPermissionError] = useState(false);
  const [uploadErrorMessageKey, setUploadErrorKey] = useState(null);

  const navigate = useNavigate();

  // Remembered across front -> back so the same rear camera is reused.
  const preferredCameraRef = useRef(null);

  // One capture attempt == one capture_session_id, so an identical restage is
  // recognised by IDV as a replay rather than a conflict.
  const captureSessionIdRef = useRef(createCaptureSessionId());

  const requiredSides = documentType ? requiredSidesFor(documentType) : [];
  const currentSide = requiredSides[sideIndex] || null;

  // Captured File objects only ever live here. Nothing leaves the browser.
  useEffect(() => {
    return () => {
      preferredCameraRef.current = null;
    };
  }, []);

  const handleDocumentTypeChosen = useCallback((chosen) => {
    setDocumentType(chosen);
    setCaptures({});
    setSideIndex(0);
    setStep(STEPS.CAMERA_PERMISSION);
  }, []);

  const handleRequestPermission = useCallback(async () => {
    setPermissionError(false);

    try {
      // Prompt once here so the capture screen opens with permission settled.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
      });
      await stopMediaStream(stream);

      setStep(STEPS.CAPTURE);
    } catch (error) {
      debugLogger.cameraAccess('denied', {
        step: 'document_capture_permission',
        name: error && error.name,
      });
      setPermissionError(true);
    }
  }, []);

  /**
   * Sends the captured documents straight to IDV.
   *
   * The upload URL and bearer token come from Certage's getConfig, minted fresh
   * here rather than at the start of capture so a long capture cannot expire
   * the token. Document bytes never pass through Certage Backend.
   */
  const uploadDocumentsToIdv = useCallback(
    async (collected, chosenType) => {
      setStep(STEPS.UPLOADING);
      setUploadErrorKey(null);

      try {
        const configResponse = await getConfig(sessionId);
        const idv = configResponse?.data?.idv;

        if (!idv?.upload_url || !idv?.upload_token) {
          setUploadErrorKey('uploadExpired');
          setStep(STEPS.CAPTURE);
          return;
        }

        const formData = buildDocumentFormData({
          documentType: chosenType,
          captures: collected,
          captureSessionId: captureSessionIdRef.current,
        });

        const response = await uploadDocuments(idv.upload_url, idv.upload_token, formData);

        if (response?.status === 202) {
          // Liveness comes next; the backend independently re-checks with IDV
          // that the documents really are staged before starting it.
          setStep(STEPS.COMPLETE);
          navigate(`/process/${sessionId}`);
          return;
        }

        debugLogger.log('Document upload rejected', { status: response?.status });

        setUploadErrorKey(uploadErrorKey(response?.status));
        setStep(STEPS.CAPTURE);
      } catch (error) {
        debugLogger.log('Document upload failed', { reason: error?.message });

        setUploadErrorKey('uploadFailed');
        setStep(STEPS.CAPTURE);
      }
    },
    [navigate, sessionId],
  );

  const handleSideComplete = useCallback(
    (side, frames) => {
      const collected = { ...captures, [side]: frames };
      setCaptures(collected);

      const next = sideIndex + 1;

      if (next >= requiredSides.length) {
        uploadDocumentsToIdv(collected, documentType);
        return;
      }

      setSideIndex(next);
    },
    [captures, documentType, requiredSides.length, sideIndex, uploadDocumentsToIdv],
  );

  const handleRestart = useCallback(() => {
    setCaptures({});
    setSideIndex(0);
    setDocumentType(null);
    setPermissionError(false);
    setStep(STEPS.SELECT_DOCUMENT_TYPE);
  }, []);

  const getPreferredDocumentCamera = useCallback(() => preferredCameraRef.current, []);

  const handleDocumentCameraSelected = useCallback((deviceId, label) => {
    preferredCameraRef.current = { deviceId, label: label || null };
  }, []);

  // -------------------------------------------------------------- rendering

  let content = null;

  if (step === STEPS.SELECT_DOCUMENT_TYPE) {
    content = <DocumentTypeSelection onContinue={handleDocumentTypeChosen} />;
  } else if (step === STEPS.CAMERA_PERMISSION) {
    content = (
      <Box className="docCapture__panel">
        <Typography variant="h1" className="docCapture__title">
          {copy.permission.title}
        </Typography>
        <Typography className="docCapture__subtitle">
          {permissionError ? copy.permission.help : copy.permission.description}
        </Typography>

        <Box className="docCapture__actions">
          <Button className="docCapture__primaryAction" onClick={handleRequestPermission}>
            {copy.permission.allow}
          </Button>
          <Button className="docCapture__secondaryAction" onClick={handleRestart}>
            {copy.capture.cancel}
          </Button>
        </Box>
      </Box>
    );
  } else if (step === STEPS.UPLOADING) {
    content = (
      <Box className="docCapture__panel">
        <Typography variant="h1" className="docCapture__title">
          {copy.capture.uploading}
        </Typography>
      </Box>
    );
  } else if (step === STEPS.CAPTURE && currentSide) {
    content = (
      <CameraCapture
        key={currentSide}
        side={currentSide}
        overlayType={overlayTypeFor(currentSide)}
        title={copy.sides[currentSide]}
        subtitle={copy.capture.instruction}
        frameCount={1}
        getPreferredDocumentCamera={getPreferredDocumentCamera}
        onDocumentCameraSelected={handleDocumentCameraSelected}
        onComplete={handleSideComplete}
        onCancel={handleRestart}
      />
    );
  } else if (step === STEPS.COMPLETE) {
    // Development-only, and deliberately metadata only: no bytes, no data URLs.
    const summary = Object.keys(captures).map((side) => ({
      side,
      frames: captures[side].length,
      bytes: captures[side].reduce((total, frame) => total + frame.file.size, 0),
    }));

    content = (
      <Box className="docCapture__panel">
        <Typography variant="h1" className="docCapture__title">
          {copy.complete.title}
        </Typography>
        <Typography className="docCapture__subtitle">{copy.complete.subtitle}</Typography>

        <Box className="docCapture__summary" data-testid="docCapture-summary">
          {summary.map((entry) => (
            <Typography key={entry.side} className="docCapture__summaryRow">
              {`${copy.sides[entry.side]}: ${entry.frames} (${Math.round(entry.bytes / 1024)} KB)`}
            </Typography>
          ))}
        </Box>

        <Box className="docCapture__actions">
          <Button className="docCapture__secondaryAction" onClick={handleRestart}>
            {copy.capture.retake}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <AppLayout>
      <Box
        className="docCapture"
        data-session-id={sessionId}
        data-step={step}
        data-upload-error={uploadErrorMessageKey || undefined}
        data-capture-mode={documentType ? captureModeFor(documentType) : undefined}
      >
        {uploadErrorMessageKey ? (
          <Typography className="docCapture__hint is-error" role="alert">
            {copy.errors[uploadErrorMessageKey]}
          </Typography>
        ) : null}

        {content}
      </Box>
    </AppLayout>
  );
}

export default DocumentCapture;
